import { useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { FaFilm, FaCamera, FaArrowRight } from "react-icons/fa";
import QuickViewtModal from "@/components/modals/quickViewModal";
import SafeImage from "@/components/SafeImage";

// Lazy-load the enquiry modal. forwardRef + useImperativeHandle in the
// source module is preserved by next/dynamic, so the parent's enquiryRef
// still works.
const SendEnquiryButton = dynamic(
  () => import("@/components/enquiry/SendEnquiryButton"),
  { ssr: false }
);
import { useDispatch } from "react-redux";
import {
  addToWishlist,
  deleteFromWishlist,
} from "@/store/slices/wishlist-slice";

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { resolveProductImage } from "@/lib/productImage";

const truncate = (text, max = 110) => {
  const value = String(text || "").trim();
  if (value.length <= max) return value;
  return value.slice(0, max).replace(/\s+\S*$/, "") + "…";
};

const ProductItem = ({
  productData,
  slug,
  baseUrl,
  discountedPrice,
  productPrice,
  cartItem,
  wishlistItem,
  compareItem,
  variant = "full", // "full" | "featured"
}) => {
  const projectStage = String(productData.project_stage || "").toLowerCase();
  const badgeText = projectStage
    ? projectStage.charAt(0).toUpperCase() + projectStage.slice(1)
    : "";
  const badgeColor = projectStage ? `project-stage-${projectStage}` : "bg-sale";

  const dispatch = useDispatch();

  const [modalShow, setModalShow] = useState(false);
  // Imperative ref so the mail-icon CTA in the hover-action row opens the
  // SendEnquiryButton modal directly (instead of falling through to QuickView).
  const enquiryRef = useRef(null);

  const wishListTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Wishlist
    </Tooltip>
  );
  const quickViewTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Quick View
    </Tooltip>
  );
  const sendEnquiryTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Send Enquiry
    </Tooltip>
  );

  const isFeatured = variant === "featured";

  return (
    <>
      <div className="ltn__product-item ltn__product-item-4">
        <div className="product-img">
          <Link href={`/${baseUrl}/${slug}`}>
            {(() => {
              const img = resolveProductImage(
                productData.productImg || productData.featured_image
              );
              if (img) {
                return (
                  <SafeImage
                    src={img}
                    alt={`${productData.title}`}
                    width={600}
                    height={400}
                    sizes="(max-width: 575px) 100vw, (max-width: 991px) 50vw, 33vw"
                    loading="lazy"
                    style={{ width: "100%", height: "auto" }}
                  />
                );
              }
              // No image — render a tasteful placeholder instead of a broken <img>.
              return (
                <span
                  className="product-img-placeholder d-inline-flex align-items-center justify-content-center"
                  aria-label={productData.title}
                  role="img"
                >
                  <FaCamera />
                </span>
              );
            })()}
          </Link>
          {badgeText ? (
            <div className="product-badge">
              <ul>
                <li className={`sale-badge ${badgeColor}`}>
                  {badgeText}
                </li>
              </ul>
            </div>
          ) : null}
          {!isFeatured && (
            <div className="product-img-location-gallery">
              <div className="product-img-location">
                <ul>
                  <li>
                    <span className="product-img-location-text">
                      <i className="flaticon-pin"></i>
                      {productData.location}
                    </span>
                  </li>
                </ul>
              </div>
              <div className="product-img-gallery">
                <ul>
                  <li>
                    <Link
                      className="d-flex align-items-center justify-content-center"
                      href={`/${baseUrl}/${slug}`}
                    >
                      <FaCamera className="me-2" />
                      {productData.photo.length}
                    </Link>
                  </li>
                  <li>
                    <Link
                      className="d-flex align-items-center justify-content-center"
                      href={`/${baseUrl}/${slug}`}
                    >
                      <FaFilm className="me-2" />
                      {productData.video.length}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
        <div className="product-info">
          <div className="product-price d-none">
            <span>
              {`$ ${productData.price}`}
              <label>/Month</label>
            </span>
          </div>
          <h2 className="product-title">
            <Link href={`/${baseUrl}/${slug}`}>{productData.title}</Link>
          </h2>
          {!isFeatured && (
            <div className="product-description">
              <p>{productData.fullDescription}</p>
            </div>
          )}
          {productData.card_blurb || productData.excerpt ? (
            <p className="ltn__product-blurb">
              {productData.card_blurb || productData.excerpt}
            </p>
          ) : null}
        </div>
        <div className="product-info-bottom">
          {isFeatured ? (
            <Link
              href={`/${baseUrl}/${slug}`}
              className="ltn__product-featured-cta"
            >
              View Details <FaArrowRight />
            </Link>
          ) : (
            <>
              {productData.agent ? (
                <div className="real-estate-agent">
                  <div className="agent-img">
                    <SafeImage
                      src={`/img/blog/${productData.agent.img}`}
                      alt={`${productData.agent.fullName}`}
                      width={80}
                      height={80}
                      loading="lazy"
                      style={{ width: "80px", height: "80px", objectFit: "cover" }}
                    />
                  </div>
                  <div className="agent-brief">
                    <span className="agent-firstname">{productData.agent.firstName}</span>
                    <small>{productData.agent.type}</small>
                  </div>
                </div>
              ) : null}
              <div className="product-hover-action">
                <ul>
                  <li>
                    <OverlayTrigger
                      placement="right"
                      delay={{ show: 250, hide: 400 }}
                      overlay={quickViewTooltip}
                    >
                      <button onClick={() => setModalShow(true)}>
                        <i className="flaticon-expand"></i>
                      </button>
                    </OverlayTrigger>
                  </li>
                  <li>
                    <OverlayTrigger
                      placement="right"
                      delay={{ show: 250, hide: 400 }}
                      overlay={wishListTooltip}
                    >
                      <button
                        onClick={
                          wishlistItem !== undefined
                            ? () => dispatch(deleteFromWishlist(productData.id))
                            : () => dispatch(addToWishlist(productData))
                        }
                      >
                        <i className="flaticon-heart-1"></i>
                      </button>
                    </OverlayTrigger>
                  </li>
                  <li>
                    <OverlayTrigger
                      placement="right"
                      delay={{ show: 250, hide: 400 }}
                      overlay={sendEnquiryTooltip}
                    >
                      <button
                        onClick={() => enquiryRef.current?.open()}
                        aria-label="Send Enquiry"
                      >
                        <i className="flaticon-mail"></i>
                      </button>
                    </OverlayTrigger>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>

      <QuickViewtModal
        productData={productData}
        show={modalShow}
        onHide={() => setModalShow(false)}
        slug={slug}
        discountedprice={discountedPrice}
        productprice={productPrice}
        cartitem={cartItem}
        wishlistitem={wishlistItem}
        compareitem={compareItem}
      />

      <div className="ltn__product-item-enquiry mt-2 d-none">
        <SendEnquiryButton ref={enquiryRef} product={productData} />
      </div>
    </>
  );
};

export default ProductItem;
